<script setup lang="ts">
// Shared empty-state - a Phosphor icon + title + body + optional primary
// action slot. Replaces the bare-text "No X yet" blocks each view used
// to hand-roll, so every empty reads the same (icon + message + CTA).
//
// `center` makes a whole-view empty sit dead-center of the content area
// (both axes), matching the Documents empty state. The host is
// display:contents by default so non-centered usages (and views that wrap
// it in their own centering container) are unaffected.
import AppIcon from '@/components/AppIcon.vue';
import type { IconName } from '@/lib/icons';

withDefaults(defineProps<{
  icon?: IconName;
  title: string;
  body?: string;
  center?: boolean;
}>(), { center: false });
</script>

<template>
  <div class="empty-state-host" :class="{ 'empty-state-host--center': center }">
    <div class="empty-state">
      <span v-if="icon" class="empty-state__tile" aria-hidden="true">
        <AppIcon :name="icon" :size="24" class="empty-state__icon" />
      </span>
      <p class="empty-state__title">{{ title }}</p>
      <p v-if="body" class="empty-state__body">{{ body }}</p>
      <div v-if="$slots.action" class="empty-state__action">
        <slot name="action" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@layer surface {
  // Passthrough by default: the host generates no box, so the card lays out
  // exactly as if there were no wrapper (existing usages unchanged).
  .empty-state-host { display: contents; }

  // Centered variant: fill the visible content area and center the card on
  // both axes. `flex: 1` fills exactly (subtitle-aware) when the view root is
  // a height-bearing flex column; the dvh min-height is the fallback for
  // auto-height roots (most views) - sized to never force a scrollbar
  // (content area is ~100dvh - header - padding; 12rem leaves margin).
  .empty-state-host--center {
    display: grid;
    place-items: center;
    flex: 1 1 auto;
    width: 100%;
    min-height: calc(100dvh - 12rem);
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    text-align: center;
    padding: var(--space-9) var(--space-6);
    background: var(--surface-panel);
    border: 1px dashed var(--border-strong);
    border-radius: var(--radius-card);

    &__tile {
      display: inline-grid;
      place-items: center;
      width: 48px;
      height: 48px;
      border-radius: var(--radius-pill);
      background: var(--surface-sunken);
      margin-bottom: var(--space-1);
    }
    &__icon { color: var(--ink-subtle); }
    &__title {
      margin: 0;
      font-size: var(--text-md);
      font-weight: 600;
      color: var(--ink-primary);
    }
    &__body {
      margin: 0;
      max-width: 46ch;
      font-size: var(--text-sm);
      color: var(--ink-muted);
      line-height: var(--leading-base);
    }
    &__action { margin-top: var(--space-3); }
  }
}
</style>
